/**
 * @file Date utility functions for ACE
 * @author Knut Sveidqvist, Andreas Hellström
 */

//------------------------------------------------------------------------------
// Requirements
//------------------------------------------------------------------------------

import { isDate } from './typeUtils';

//------------------------------------------------------------------------------
// Definitions
//------------------------------------------------------------------------------

const MILLISECONDS_IN_HOUR = 60 * 60 * 1000;
const MILLISECONDS_IN_MINUTE = 60 * 1000;
const MILLISECONDS_IN_SECOND = 1000;

//------------------------------------------------------------------------------
// Exported functions
//------------------------------------------------------------------------------

/**
 * Check if object is of JavaScript date type and not an invalid date.
 * @param {*} obj
 * @returns {boolean} Returns true if object is a valid JavaScript date.
 */
export function isValidDate(obj) {
  return isDate(obj) && obj.toString && obj.toString() !== 'Invalid Date';
}

/** Returns a new date object with current date and time.
 * @returns {Date}
 */
export function now() {
  return new Date();
}

/**
 * Returns a new date object where date is same as source date with time added to it.
 * @param {number} hours - Hours to add
 * @param {number} minutes - Minutes to add
 * @param {number} seconds - Seconds to add
 * @param {number} milliseconds - Milliseconds to add
 * @param {Date} someDate
 * @returns {Date}
 */
export function addTimeToDate({ hours = 0, minutes = 0, seconds = 0, milliseconds = 0 }, someDate) {
  const millisecondsToAdd =
    hours * MILLISECONDS_IN_HOUR +
    minutes * MILLISECONDS_IN_MINUTE +
    seconds * MILLISECONDS_IN_SECOND +
    milliseconds;
  const clonedDate = new Date(someDate.getTime());
  clonedDate.setTime(clonedDate.getTime() + millisecondsToAdd);

  return clonedDate;
}

/**
 * Returns a new date object where date is same as source date with time set to start of day.
 * @param {Date} someDate
 * @returns {Date}
 */
export function setTimeToStartOfDay(someDate) {
  const newDate = new Date(someDate.getTime());
  newDate.setHours(0, 0, 0, 0);
  return newDate;
}

/**
 * Returns short date string for a source date.
 * @param {Date} someDate
 * @returns {string}
 * @example
 * // dateToShortDateString(new Date('2019-01-31 11:30:00'))
 * // Result: '2019-01-31'
 */
export function dateToShortDateString(someDate) {
  return `${someDate.getFullYear()}-${
    // date.getMonth() return 0-11 for January to December
    (someDate.getMonth() + 1).toString().padStart(2, '0')
  }-${someDate.getDate().toString().padStart(2, '0')}`;
}
